// The lobby with a pad (#174): the stick or d-pad moves focus to the nearest
// control that way, ✕ (A) presses it and Start drops in; ○ (B) closes
// what's over it (What's new, #185). DOM-free so it's
// testable; `Lobby` polls the pad and feeds it.

export type NavDir = "left" | "right" | "up" | "down";
export type PadNav = NavDir | "press" | "go" | "back";

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A standard-mapping pad's state, as `Lobby` reads it each frame. */
export interface PadRead {
  buttons: readonly boolean[];
  axes: readonly number[];
}

/** Stick past this counts as a push. */
const STICK = 0.5;

/** What one pad direction or button reads as this frame, or null. */
function held(p: PadRead): Set<PadNav> {
  const out = new Set<PadNav>();
  const [ax = 0, ay = 0] = p.axes;
  if (p.buttons[14] || ax < -STICK) out.add("left");
  if (p.buttons[15] || ax > STICK) out.add("right");
  if (p.buttons[12] || ay < -STICK) out.add("up");
  if (p.buttons[13] || ay > STICK) out.add("down");
  if (p.buttons[0]) out.add("press");
  if (p.buttons[9]) out.add("go");
  if (p.buttons[1]) out.add("back");
  return out;
}

/** What was newly pushed or pressed between two reads (edges only, so holding the stick moves once). */
export function padEdges(prev: PadRead | null, now: PadRead): PadNav[] {
  const before = prev ? held(prev) : new Set<PadNav>();
  return [...held(now)].filter((n) => !before.has(n));
}

/**
 * The box to move to from `from` going `dir`: the nearest one whose centre
 * lies that way, distance along the way counted once and sideways twice, so
 * a control straight ahead beats a closer one off to the side. -1 if none.
 */
export function nearestBox(boxes: readonly Box[], from: number, dir: NavDir): number {
  const a = boxes[from];
  if (!a) return boxes.length ? 0 : -1;
  const ax = a.x + a.w / 2;
  const ay = a.y + a.h / 2;
  let best = -1;
  let bestScore = Infinity;
  boxes.forEach((b, i) => {
    if (i === from) return;
    const dx = b.x + b.w / 2 - ax;
    const dy = b.y + b.h / 2 - ay;
    const along = dir === "left" ? -dx : dir === "right" ? dx : dir === "up" ? -dy : dy;
    const side = dir === "left" || dir === "right" ? Math.abs(dy) : Math.abs(dx);
    if (along <= 1) return;
    const score = along + 2 * side;
    if (score < bestScore) {
      bestScore = score;
      best = i;
    }
  });
  return best;
}
